import { cn } from 'components/UI/cn'

interface ToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  /** `sm` (default) is 36×20 with a 16×16 thumb; `md` is 44×24 with a 20×20 thumb. */
  size?: 'sm' | 'md'
  'aria-label'?: string
  className?: string
}

const TRACK_SIZE = {
  sm: 'h-5 w-9',
  md: 'h-6 w-11',
} as const

const THUMB_SIZE = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
} as const

// Track interior = track width - p-0.5 (2px each side). Thumb travels
// trackInterior - thumbWidth: sm → 32 - 16 = 16px (translate-x-4),
// md → 40 - 20 = 20px (translate-x-5).
const THUMB_TRANSLATE = {
  sm: 'translate-x-4',
  md: 'translate-x-5',
} as const

/**
 * DS2026 toggle. Spec: Figma node 2499:31600 in the First Bite Design System.
 *
 * Off bg: #e5e7eb (neutral-200). On bg: #087443 (brand-active / Basil 700).
 * Disabled bg: #f3f4f6 (neutral-100) regardless of checked state. Thumb is
 * white with a small drop shadow. Focus ring is a 4px shadow at 20%
 * brand-active.
 */
export function Toggle({
  checked,
  onChange,
  disabled,
  size = 'sm',
  'aria-label': ariaLabel,
  className,
}: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-xl p-0.5 transition-colors',
        'focus-visible:outline-none focus-visible:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]',
        TRACK_SIZE[size],
        disabled
          ? 'cursor-not-allowed bg-neutral-100'
          : checked
            ? 'cursor-pointer bg-brand-active'
            : 'cursor-pointer bg-neutral-200',
        className
      )}
    >
      <span
        className={cn(
          'pointer-events-none inline-block rounded-full bg-white shadow-[0_1px_2px_0_rgba(16,24,40,0.06),0_1px_3px_0_rgba(16,24,40,0.10)] transition-transform',
          THUMB_SIZE[size],
          checked ? THUMB_TRANSLATE[size] : 'translate-x-0'
        )}
      />
    </button>
  )
}
